import type { Actor, ActorId, Flow, FlowParticipation, FlowStep } from '@/types';

export { tryEditorHref, messageWorkspaceHref, sampleWorkspaceHref } from '../lib/messageWorkspace';
export type { FlowParticipation };

export const ACTORS: Record<ActorId, Actor> = {
  psu: { id: 'psu', label: 'PSU', sublabel: 'Payment service user' },
  tpp: { id: 'tpp', label: 'TPP', sublabel: 'AISP / PISP' },
  aspsp: { id: 'aspsp', label: 'ASPSP', sublabel: 'Account servicing bank' },
  sca: { id: 'sca', label: 'SCA', sublabel: 'Authentication service' },
  csm: { id: 'csm', label: 'CSM', sublabel: 'Clearing & settlement' },
  beneficiary: { id: 'beneficiary', label: 'Creditor bank', sublabel: 'Beneficiary side' },
  rail: { id: 'rail', label: 'SIC rail', sublabel: 'SIC / euroSIC / SIC IP' },
  scheme: { id: 'scheme', label: 'A2A overlay', sublabel: 'Retail A2A scheme (e.g. Wero)' },
  'external-network': { id: 'external-network', label: 'External Network', sublabel: 'Inbound message rail / SWIFT / STEP2' },
  agi: { id: 'agi', label: 'AGI', sublabel: 'Access Gateway Interface' },
  'payment-hub': { id: 'payment-hub', label: 'Payment Hub', sublabel: 'Orchestration & Routing engine' },
  ilm: { id: 'ilm', label: 'ILM', sublabel: 'Intraday Liquidity Management' },
  settlement: { id: 'settlement', label: 'Settlement Engine', sublabel: 'Core ledger / Central bank settlement' },
};

export const FLOWS: Flow[] = [
  // ────────────────────────────────────────────────────────────────────────
  {
    id: 'bg-ais-consent-redirect',
    name: 'Account access with redirect SCA',
    standardId: 'berlin-group',
    category: 'account-information',
    summary:
      'The canonical AIS journey: create a consent, hand the PSU to the bank for authentication, then read data until the consent expires.',
    useCase:
      'Every account aggregator starts here. Most integration failures are in steps 2 and 5 — the redirect contract and the moment the consent flips to valid.',
    actors: ['psu', 'tpp', 'aspsp', 'sca'],
    tags: ['ais', 'consent', 'redirect', 'berlin group'],
    steps: [
      {
        n: 1,
        from: 'psu',
        to: 'tpp',
        layer: 'api',
        label: 'Pick a bank, agree to share',
        detail:
          'The TPP shows which data it wants and for how long. This screen is the legal basis for the access field you are about to send — keep them in sync or you will fail an audit.',
      },
      {
        n: 2,
        from: 'tpp',
        to: 'aspsp',
        layer: 'api',
        method: 'POST',
        path: '/v1/consents',
        httpStatus: 201,
        label: 'Create the consent resource',
        detail:
          'The access object decides everything that follows. availableAccounts gets you the list only; balances and transactions each need their own array. validUntil is capped by the ASPSP regardless of what you ask for.',
        headers: ['X-Request-ID', 'PSU-IP-Address', 'TPP-Redirect-URI', 'TPP-Nok-Redirect-URI'],
        sampleId: 'bg-consent-request',
        codes: ['CONSENT_INVALID', 'FORMAT_ERROR', 'SERVICE_BLOCKED'],
      },
      {
        n: 3,
        from: 'aspsp',
        to: 'tpp',
        layer: 'api',
        httpStatus: 201,
        label: 'consentId + scaRedirect link',
        detail:
          'consentStatus is received. The _links object tells you which SCA approach the bank chose — scaRedirect, scaOAuth or startAuthorisation. Branch on the link, never on documentation.',
        sampleId: 'bg-consent-response',
        codes: ['received'],
      },
      {
        n: 4,
        from: 'psu',
        to: 'sca',
        layer: 'api',
        label: 'Authenticate at the bank',
        detail:
          'Browser or app-to-app redirect. You lose control here: the PSU can abandon, the link can expire, the bank can change the account selection. Treat your callback as untrusted input.',
      },
      {
        n: 5,
        from: 'tpp',
        to: 'aspsp',
        layer: 'api',
        method: 'GET',
        path: '/v1/consents/{consentId}/status',
        httpStatus: 200,
        label: 'Poll until the consent is valid',
        detail:
          'Poll the status endpoint, not the full consent — it is cheaper and some banks rate-limit the latter. Terminal states are valid, rejected, revokedByPsu and expired.',
        codes: ['valid', 'revokedByPsu', 'expired', 'CONSENT_EXPIRED'],
      },
      {
        n: 6,
        from: 'tpp',
        to: 'aspsp',
        layer: 'api',
        method: 'GET',
        path: '/v1/accounts',
        httpStatus: 200,
        label: 'List accounts',
        detail:
          'Returns resourceIds, which are opaque and per-consent. Do not cache them across consents; do not assume they are IBANs.',
        sampleId: 'bg-accounts-response',
        codes: ['CONSENT_INVALID', 'CONSENT_EXPIRED'],
      },
      {
        n: 7,
        from: 'tpp',
        to: 'aspsp',
        layer: 'api',
        method: 'GET',
        path: '/v1/accounts/{accountId}/transactions',
        httpStatus: 200,
        label: 'Read transactions',
        detail:
          'bookingStatus=both gets booked and pending in one call. Set Accept to application/xml and a compliant ASPSP returns camt.052 instead of JSON — useful when you already have an ISO parser.',
        sampleId: 'camt-052-report',
        messageShort: 'camt.052',
        codes: ['ACCESS_EXCEEDED', 'PERIOD_INVALID'],
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────────────
  {
    id: 'bg-pis-sepa-redirect',
    name: 'SEPA credit transfer with decoupled SCA',
    standardId: 'berlin-group',
    category: 'payment-initiation',
    summary:
      'A single SEPA payment initiated by a PISP, authorised in the bank app rather than a browser redirect. The status endpoint is the only source of truth.',
    useCase:
      'Decoupled SCA is growing because it survives mobile browsers. The cost is polling and a much longer worst-case wait.',
    actors: ['psu', 'tpp', 'aspsp', 'sca', 'csm'],
    tags: ['pis', 'sepa', 'decoupled', 'berlin group'],
    steps: [
      {
        n: 1,
        from: 'tpp',
        to: 'aspsp',
        layer: 'api',
        method: 'POST',
        path: '/v1/payments/sepa-credit-transfers',
        httpStatus: 201,
        label: 'Initiate the payment',
        detail:
          'TPP-Explicit-Authorisation-Preferred=true asks for a separate authorisation sub-resource, which you want if you plan to offer SCA method selection.',
        headers: ['X-Request-ID', 'PSU-ID', 'TPP-Explicit-Authorisation-Preferred'],
        sampleId: 'bg-payment-request',
        codes: ['RCVD', 'FORMAT_ERROR', 'PRODUCT_INVALID'],
      },
      {
        n: 2,
        from: 'tpp',
        to: 'aspsp',
        layer: 'api',
        method: 'POST',
        path: '/v1/payments/sepa-credit-transfers/{paymentId}/authorisations',
        httpStatus: 201,
        label: 'Start the authorisation',
        detail: 'Returns an authorisationId and the available SCA methods when there is more than one.',
        codes: ['received', 'SCA_METHOD_UNKNOWN'],
      },
      {
        n: 3,
        from: 'tpp',
        to: 'aspsp',
        layer: 'api',
        method: 'PUT',
        path: '/v1/payments/sepa-credit-transfers/{paymentId}/authorisations/{authorisationId}',
        httpStatus: 200,
        label: 'Select the SCA method',
        detail: 'scaStatus moves to scaMethodSelected, then to started once the bank has pushed the challenge to the PSU device.',
        codes: ['scaMethodSelected', 'started'],
      },
      {
        n: 4,
        from: 'aspsp',
        to: 'psu',
        layer: 'api',
        label: 'Push challenge to the bank app',
        detail: 'Out of band. The PSU may never open the notification, so treat abandonment as the default outcome and set a deadline.',
      },
      {
        n: 5,
        from: 'tpp',
        to: 'aspsp',
        layer: 'api',
        method: 'GET',
        path: '/v1/payments/sepa-credit-transfers/{paymentId}/status',
        httpStatus: 200,
        label: 'Poll the transaction status',
        detail:
          'Back off exponentially. ACSP means accepted for settlement — the money is committed but not yet cleared. Only ACSC is final.',
        codes: ['ACTC', 'ACSP', 'ACSC', 'RJCT', 'CANC'],
      },
      {
        n: 6,
        from: 'aspsp',
        to: 'csm',
        layer: 'clearing',
        label: 'pacs.008 into clearing',
        detail:
          'The API layer ends here. What the TPP called a payment becomes an interbank credit transfer, and every downstream error arrives as an ISO reason code rather than an HTTP status.',
        messageShort: 'pacs.008',
        sampleId: 'pacs-008-sct',
        codes: ['AC01', 'AC04', 'AM04'],
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────────────
  {
    id: 'stet-payment-request',
    name: 'STET payment request and confirmation',
    standardId: 'stet',
    category: 'payment-initiation',
    summary:
      'The French profile. Two calls that matter: create the payment request, then confirm it after SCA. Skipping the confirmation is the single most common STET bug.',
    useCase: 'Any PISP operating in France. The HTTP Signature requirement makes this the least forgiving standard to debug.',
    actors: ['psu', 'tpp', 'aspsp', 'sca'],
    tags: ['pisp', 'stet', 'france', 'http signature'],
    steps: [
      {
        n: 1,
        from: 'tpp',
        to: 'aspsp',
        layer: 'api',
        method: 'POST',
        path: '/oauth2/token',
        httpStatus: 200,
        label: 'Get a client_credentials token',
        detail: 'grant_type=client_credentials with scope=pisp. The token is bound to the QWAC used for mTLS.',
        codes: ['invalid_client', 'invalid_scope'],
      },
      {
        n: 2,
        from: 'tpp',
        to: 'aspsp',
        layer: 'api',
        method: 'POST',
        path: '/v1/payment-requests',
        httpStatus: 201,
        label: 'Create the payment request',
        detail:
          'The body mirrors pain.001: paymentInformationId, creditTransferTransaction array, beneficiary block. Sign (request-target), date, digest and x-request-id with your QSealC.',
        headers: ['Signature', 'Digest', 'X-Request-ID'],
        sampleId: 'stet-payment-request',
        codes: ['NOK', 'signature_invalid'],
      },
      {
        n: 3,
        from: 'psu',
        to: 'sca',
        layer: 'api',
        label: 'Authenticate via the consentApprovalUrl',
        detail: 'The _links.consentApproval href from the 201. Redirect the PSU there and wait for your callback.',
      },
      {
        n: 4,
        from: 'tpp',
        to: 'aspsp',
        layer: 'api',
        method: 'POST',
        path: '/v1/payment-requests/{id}/confirmation',
        httpStatus: 200,
        label: 'Confirm the payment request',
        detail:
          'Without this call the request sits unconfirmed and expires. In decoupled mode the body carries psuAuthenticationFactor; in redirect mode it must be empty.',
        codes: ['ACSC', 'ACSP', 'RJCT'],
      },
      {
        n: 5,
        from: 'tpp',
        to: 'aspsp',
        layer: 'api',
        method: 'GET',
        path: '/v1/payment-requests/{id}',
        httpStatus: 200,
        label: 'Read the final status',
        detail: 'paymentInformationStatus at the top, individual transaction statuses underneath. Read both.',
        codes: ['ACSC', 'PDNG', 'RJCT'],
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────────────
  {
    id: 'ukob-domestic-payment',
    name: 'UK domestic payment with detached JWS',
    standardId: 'uk-open-banking',
    category: 'payment-initiation',
    summary:
      'Consent, funds check, submission. The consent and payment bodies must be byte-identical in their Initiation blocks, and both write calls carry a detached JWS.',
    useCase: 'UK PISP work. The strictness pays off: once conformant, behaviour across banks is remarkably uniform.',
    actors: ['psu', 'tpp', 'aspsp', 'sca'],
    tags: ['pisp', 'uk', 'jws', 'fapi', 'faster payments'],
    steps: [
      {
        n: 1,
        from: 'tpp',
        to: 'aspsp',
        layer: 'api',
        method: 'POST',
        path: '/domestic-payment-consents',
        httpStatus: 201,
        label: 'Create the payment consent',
        detail:
          'Initiation plus a Risk block. x-jws-signature is a detached JWS over the body with b64=false and the OB claims in the header.',
        headers: ['x-fapi-interaction-id', 'x-idempotency-key', 'x-jws-signature'],
        sampleId: 'ukob-payment-consent',
        codes: ['UK.OBIE.Field.Missing', 'UK.OBIE.Signature.Invalid'],
      },
      {
        n: 2,
        from: 'psu',
        to: 'sca',
        layer: 'api',
        label: 'Authorise via OIDC with intent binding',
        detail:
          'PAR + PKCE, request object signed, openbanking_intent_id set to the ConsentId. App-to-app redirect where the bank supports it.',
      },
      {
        n: 3,
        from: 'tpp',
        to: 'aspsp',
        layer: 'api',
        method: 'GET',
        path: '/domestic-payment-consents/{ConsentId}/funds-confirmation',
        httpStatus: 200,
        label: 'Confirm funds',
        detail: 'Boolean only, valid only after authorisation. A false here saves you a rejected payment.',
        codes: ['UK.OBIE.Resource.InvalidConsentStatus'],
      },
      {
        n: 4,
        from: 'tpp',
        to: 'aspsp',
        layer: 'api',
        method: 'POST',
        path: '/domestic-payments',
        httpStatus: 201,
        label: 'Submit the payment',
        detail:
          'The Initiation block must match the consent exactly. Fresh x-idempotency-key, and retry with the same key rather than a new one.',
        codes: ['UK.OBIE.Rules.FailsControlParameters', 'UK.OBIE.Reauthenticate'],
      },
      {
        n: 5,
        from: 'tpp',
        to: 'aspsp',
        layer: 'api',
        method: 'GET',
        path: '/domestic-payments/{DomesticPaymentId}',
        httpStatus: 200,
        label: 'Read the status',
        detail:
          'Pending, AcceptedSettlementInProgress, AcceptedCreditSettlementCompleted or Rejected. These are OBIE status names, not ISO codes — do not try to map them one-to-one onto ACSP and ACSC.',
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────────────
  {
    id: 'bg-funds-confirmation',
    name: 'Funds confirmation (PIIS)',
    standardId: 'berlin-group',
    category: 'funds-confirmation',
    summary: 'One call, one boolean. No amount, no balance, no history.',
    useCase: 'Card issuers checking coverage before authorising. The narrowest scope in PSD2 and the easiest to get approved.',
    actors: ['tpp', 'aspsp'],
    tags: ['piis', 'cbpii', 'funds', 'berlin group'],
    steps: [
      {
        n: 1,
        from: 'tpp',
        to: 'aspsp',
        layer: 'api',
        method: 'POST',
        path: '/v1/funds-confirmations',
        httpStatus: 200,
        label: 'Ask whether funds are available',
        detail:
          'Requires a PIIS consent established outside the API — usually at card issuance. The cardNumber is optional but most banks want it for matching.',
        sampleId: 'bg-funds-confirmation',
        codes: ['NO_PIIS_ACTIVATION', 'CARD_INVALID'],
      },
      {
        n: 2,
        from: 'aspsp',
        to: 'tpp',
        layer: 'api',
        httpStatus: 200,
        label: 'fundsAvailable: true | false',
        detail: 'That is the entire response body. Anything more would breach the data minimisation rule in the RTS.',
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────────────
  {
    id: 'clearing-sct-happy-path',
    name: 'Clearing leg: initiation to settlement',
    standardId: 'berlin-group',
    category: 'clearing',
    summary:
      'What happens after the API call succeeds. pain.001 becomes pacs.008, the CSM answers with pacs.002, and both sides get notified.',
    useCase:
      'Read this when a payment shows ACSP in the API but the beneficiary says nothing arrived. The answer is always in this leg.',
    actors: ['tpp', 'aspsp', 'csm', 'beneficiary', 'psu'],
    tags: ['iso20022', 'clearing', 'sepa', 'settlement'],
    steps: [
      {
        n: 1,
        from: 'tpp',
        to: 'aspsp',
        layer: 'clearing',
        label: 'pain.001 initiation received',
        detail: 'Whether it arrived as JSON or XML, the debtor bank normalises it into a pain.001 internally.',
        messageShort: 'pain.001',
        sampleId: 'pain-001-sct',
      },
      {
        n: 2,
        from: 'aspsp',
        to: 'csm',
        layer: 'clearing',
        label: 'pacs.008 submitted',
        detail:
          'SttlmMtd=CLRG for SEPA. IntrBkSttlmAmt replaces InstdAmt, and the TxId assigned here is the identifier the CSM will quote back at you.',
        messageShort: 'pacs.008',
        sampleId: 'pacs-008-sct',
      },
      {
        n: 3,
        from: 'csm',
        to: 'beneficiary',
        layer: 'clearing',
        label: 'pacs.008 forwarded',
        detail: 'The creditor bank validates the IBAN, screens against sanctions lists and checks the account is open.',
        messageShort: 'pacs.008',
        sampleId: 'pacs-008-sct',
      },
      {
        n: 4,
        from: 'beneficiary',
        to: 'csm',
        layer: 'clearing',
        label: 'pacs.002 — TxSts ACSC',
        detail: 'Accepted, settlement completed. Under SEPA Instant this has to come back inside ten seconds end to end.',
        messageShort: 'pacs.002',
        sampleId: 'pacs-002-accepted',
        codes: ['ACSC'],
      },
      {
        n: 5,
        from: 'csm',
        to: 'aspsp',
        layer: 'clearing',
        label: 'pacs.002 relayed to the debtor bank',
        detail: 'Only now can the API layer move the payment from ACSP to ACSC.',
        messageShort: 'pacs.002',
        sampleId: 'pacs-002-accepted',
        codes: ['ACSC'],
      },
      {
        n: 6,
        from: 'beneficiary',
        to: 'psu',
        layer: 'clearing',
        label: 'camt.054 credit notification',
        detail: 'What a merchant subscribes to in order to release goods without waiting for the end-of-day statement.',
        messageShort: 'camt.054',
        sampleId: 'camt-054-credit',
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────────────
  {
    id: 'clearing-reject',
    name: 'Rejection: reading a pacs.002 RJCT',
    standardId: 'berlin-group',
    category: 'exception',
    summary: 'A payment refused before settlement. The reason code tells you whether to retry, fix data, or stop entirely.',
    useCase:
      'The reject taxonomy is the most useful thing to internalise in payments. AC01 is a data bug you can fix; AM04 is a customer problem; MS03 tells you nothing on purpose.',
    actors: ['tpp', 'aspsp', 'csm', 'beneficiary'],
    tags: ['rejection', 'reason code', 'pacs.002', 'exception'],
    steps: [
      {
        n: 1,
        from: 'aspsp',
        to: 'csm',
        layer: 'clearing',
        label: 'pacs.008 submitted',
        detail: 'Nothing looks wrong yet — the debtor bank accepted the instruction.',
        messageShort: 'pacs.008',
        sampleId: 'pacs-008-sct',
      },
      {
        n: 2,
        from: 'beneficiary',
        to: 'csm',
        layer: 'clearing',
        label: 'pacs.002 — TxSts RJCT, Rsn AC01',
        detail:
          'StsRsnInf/Rsn/Cd carries the External Status Reason code. AC01 means the account identifier is wrong: the IBAN does not exist at that bank. Never retry unchanged.',
        messageShort: 'pacs.002',
        sampleId: 'pacs-002-rejected',
        codes: ['RJCT', 'AC01'],
      },
      {
        n: 3,
        from: 'csm',
        to: 'aspsp',
        layer: 'clearing',
        label: 'Reject relayed, funds released',
        detail: 'The debtor bank reverses its internal reservation. The API layer surfaces this as transactionStatus RJCT.',
        messageShort: 'pacs.002',
        codes: ['RJCT'],
      },
      {
        n: 4,
        from: 'aspsp',
        to: 'tpp',
        layer: 'api',
        method: 'GET',
        path: '/v1/payments/sepa-credit-transfers/{paymentId}/status',
        httpStatus: 200,
        label: 'TPP sees RJCT',
        detail:
          'Berlin Group has no field for the ISO reason code in the status response. If you need AC01 rather than "rejected", you need the bank reporting channel — this asymmetry is a genuine gap in the standard.',
        codes: ['RJCT'],
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────────────
  {
    id: 'clearing-recall',
    name: 'Recall: camt.056 to pacs.004',
    standardId: 'berlin-group',
    category: 'exception',
    summary:
      'Getting settled money back. The debtor bank requests cancellation, the creditor bank answers with a resolution, and if positive the funds return as a pacs.004.',
    useCase:
      'Fraud and duplicate handling. Note that the creditor bank is not obliged to agree — a recall is a request, not an instruction.',
    actors: ['aspsp', 'csm', 'beneficiary'],
    tags: ['recall', 'camt.056', 'pacs.004', 'fraud', 'exception'],
    steps: [
      {
        n: 1,
        from: 'aspsp',
        to: 'csm',
        layer: 'clearing',
        label: 'camt.056 cancellation request',
        detail:
          'CxlRsnInf/Rsn/Cd carries DUPL, TECH, FRAD or CUST. Under the SEPA rulebook the request must be raised within 13 months, and within 10 days for a technical duplicate.',
        messageShort: 'camt.056',
        sampleId: 'camt-056-recall',
        codes: ['DUPL', 'TECH', 'FRAD', 'CUST'],
      },
      {
        n: 2,
        from: 'csm',
        to: 'beneficiary',
        layer: 'clearing',
        label: 'Request forwarded',
        detail: 'The creditor bank checks whether the funds are still there and whether it needs its customer to agree.',
        messageShort: 'camt.056',
      },
      {
        n: 3,
        from: 'beneficiary',
        to: 'csm',
        layer: 'clearing',
        label: 'camt.029 resolution',
        detail:
          'Sts/Conf=ACCR when accepted. A negative answer carries a rejection reason such as ARDT (already returned), NOAS (no answer from customer) or LEGL (legal decision).',
        messageShort: 'camt.029',
        sampleId: 'camt-029-resolution',
        codes: ['ACCR', 'ARDT', 'NOAS', 'LEGL', 'NOOR'],
      },
      {
        n: 4,
        from: 'beneficiary',
        to: 'csm',
        layer: 'clearing',
        label: 'pacs.004 return',
        detail:
          'Only after a positive camt.029. RtrRsnInf/Rsn/Cd echoes the original cancellation reason so reconciliation can match it back.',
        messageShort: 'pacs.004',
        sampleId: 'pacs-004-return',
        codes: ['FOCR'],
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────────────
  {
    id: 'sct-inst-happy-path',
    name: 'SCT Inst happy path (≤10s)',
    standardId: 'sct-inst',
    category: 'payment-initiation',
    summary:
      'Full instant euro credit transfer: PISP initiates instant-sepa-credit-transfers, debtor bank submits pacs.008 with LclInstrm=INST via TIPS/RT1, creditor confirms ACSC, both sides notify.',
    useCase:
      'The baseline Instant Payments Regulation journey. If anything is slower than ten seconds end-to-end, you are off the scheme SLA.',
    actors: ['psu', 'tpp', 'aspsp', 'csm', 'beneficiary'],
    tags: ['instant', 'sct-inst', 'tips', 'rt1', 'ipr', 'pacs.008', 'pacs.002'],
    steps: [
      {
        n: 1,
        from: 'psu',
        to: 'tpp',
        layer: 'api',
        label: 'Choose instant transfer',
        detail:
          'PSU opts into instant (or IPR makes it the default for euro CT). Show fee parity vs standard SCT where required.',
      },
      {
        n: 2,
        from: 'tpp',
        to: 'aspsp',
        layer: 'api',
        method: 'POST',
        path: '/v1/payments/instant-sepa-credit-transfers',
        httpStatus: 201,
        label: 'Initiate SCT Inst via XS2A',
        detail:
          'Berlin Group product path. Same JSON shape as sepa-credit-transfers; the product segment selects INST processing.',
        sampleId: 'bg-instant-payment-request',
        codes: ['RCVD', 'PRODUCT_INVALID', 'PAYMENT_FAILED'],
        headers: ['X-Request-ID', 'PSU-IP-Address', 'TPP-Redirect-URI'],
      },
      {
        n: 3,
        from: 'psu',
        to: 'aspsp',
        layer: 'api',
        label: 'SCA / authorisation',
        detail:
          'Time of receipt / authorisation starts the scheme clock (AT-T056). Do not start your UX timer earlier.',
        codes: ['ACTC', 'ACCP'],
      },
      {
        n: 4,
        from: 'aspsp',
        to: 'csm',
        layer: 'clearing',
        label: 'pacs.008 INST → TIPS/RT1',
        detail:
          'Funds reserved immediately. LclInstrm/Cd=INST; ClrSys points at TIPS or RT1. Acceptance window is measured in seconds.',
        messageShort: 'pacs.008',
        sampleId: 'pacs-008-sct-inst',
      },
      {
        n: 5,
        from: 'csm',
        to: 'beneficiary',
        layer: 'clearing',
        label: 'Forward to creditor PSP',
        detail: 'Creditor PSP validates IBAN, AML and account status under the instant SLA.',
        messageShort: 'pacs.008',
        sampleId: 'pacs-008-sct-inst',
      },
      {
        n: 6,
        from: 'beneficiary',
        to: 'csm',
        layer: 'clearing',
        label: 'pacs.002 ACSC acknowledgement',
        detail: 'Funds made available to the beneficiary. Only ACSC ends the happy path inside the rulebook.',
        messageShort: 'pacs.002',
        sampleId: 'pacs-002-sct-inst',
        codes: ['ACSC'],
      },
      {
        n: 7,
        from: 'csm',
        to: 'aspsp',
        layer: 'clearing',
        label: 'Confirmation to debtor PSP',
        detail: 'Debtor bank releases the reservation as settled and can move API status to ACSC.',
        messageShort: 'pacs.002',
        sampleId: 'pacs-002-sct-inst',
        codes: ['ACSC'],
      },
      {
        n: 8,
        from: 'beneficiary',
        to: 'psu',
        layer: 'clearing',
        label: 'camt.054 credit notification',
        detail: 'Merchant / beneficiary side trigger to release goods without waiting for EOD.',
        messageShort: 'camt.054',
        sampleId: 'camt-054-credit',
      },
      {
        n: 9,
        from: 'aspsp',
        to: 'tpp',
        layer: 'api',
        method: 'GET',
        path: '/v1/payments/instant-sepa-credit-transfers/{paymentId}/status',
        httpStatus: 200,
        label: 'API status ACSC + notify PSU',
        detail:
          'IPR requires the originator PSP to inform the payer of the outcome. PISP UIs should surface ACSC, not linger on PDNG.',
        sampleId: 'bg-instant-payment-status-acsc',
        codes: ['ACSC'],
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────────────
  {
    id: 'sct-inst-reject',
    name: 'SCT Inst reject inside the window',
    standardId: 'sct-inst',
    category: 'exception',
    summary:
      'Creditor PSP refuses within the 10-second SLA. Debtor bank reverses the reservation and returns RJCT + reason to the TPP.',
    useCase:
      'Distinguish hard data errors (AC01) from reachability (AB05) and funds (AM04) — only some are safe to retry as SCT.',
    actors: ['tpp', 'aspsp', 'csm', 'beneficiary'],
    tags: ['instant', 'sct-inst', 'reject', 'rjct', 'exception'],
    steps: [
      {
        n: 1,
        from: 'aspsp',
        to: 'csm',
        layer: 'clearing',
        label: 'pacs.008 INST submitted',
        detail: 'Reservation placed; clock running.',
        messageShort: 'pacs.008',
        sampleId: 'pacs-008-sct-inst',
      },
      {
        n: 2,
        from: 'beneficiary',
        to: 'csm',
        layer: 'clearing',
        label: 'pacs.002 RJCT',
        detail:
          'Negative confirmation still inside the SLA. Reason code decides the next action — never auto-retry INST blindly.',
        messageShort: 'pacs.002',
        sampleId: 'pacs-002-sct-inst-reject',
        codes: ['RJCT', 'AC01', 'AM04', 'AG01', 'AB05', 'AC03'],
      },
      {
        n: 3,
        from: 'csm',
        to: 'aspsp',
        layer: 'clearing',
        label: 'Reject relayed',
        detail: 'Debtor bank lifts the reservation immediately.',
        messageShort: 'pacs.002',
        codes: ['RJCT'],
      },
      {
        n: 4,
        from: 'aspsp',
        to: 'tpp',
        layer: 'api',
        method: 'GET',
        path: '/v1/payments/instant-sepa-credit-transfers/{paymentId}/status',
        httpStatus: 200,
        label: 'API RJCT + tppMessages',
        detail:
          'Surface the ISO reason when the ASPSP exposes it. AB05 → offer standard SCT; AC01 → fix IBAN; AM04 → ask PSU.',
        sampleId: 'bg-instant-payment-status-rjct',
        codes: ['RJCT', 'AB05', 'PRODUCT_INVALID'],
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────────────
  {
    id: 'sct-inst-vop',
    name: 'SCT Inst with Verification of Payee',
    standardId: 'sct-inst',
    category: 'payment-initiation',
    summary:
      'Instant Payments Regulation path: run VoP (acmt.023/024) before initiating the instant credit transfer, then settle on SCT Inst.',
    useCase:
      'Mandatory payee check for euro instant CT. Close-match must be shown; no-match needs explicit PSU risk acceptance.',
    actors: ['psu', 'tpp', 'aspsp', 'beneficiary', 'csm'],
    tags: ['instant', 'sct-inst', 'vop', 'ipr', 'acmt.023'],
    steps: [
      {
        n: 1,
        from: 'psu',
        to: 'tpp',
        layer: 'api',
        label: 'Enter payee name + IBAN',
        detail: 'Capture the typed name — VoP compares what the PSU entered, not a normalised cache.',
      },
      {
        n: 2,
        from: 'aspsp',
        to: 'beneficiary',
        layer: 'clearing',
        label: 'acmt.023 VoP request',
        detail: 'IPR Verification of Payee before the instant order is authorised.',
        messageShort: 'acmt.023',
        sampleId: 'acmt-023-vop',
      },
      {
        n: 3,
        from: 'beneficiary',
        to: 'aspsp',
        layer: 'clearing',
        label: 'acmt.024 match report',
        detail: 'MTCH / CMTC / NMTC. On CMTC show the suggested legal name; on NMTC require explicit continue.',
        messageShort: 'acmt.024',
        sampleId: 'acmt-024-vop-report',
        codes: ['MTCH', 'CMTC', 'NMTC'],
      },
      {
        n: 4,
        from: 'tpp',
        to: 'aspsp',
        layer: 'api',
        method: 'POST',
        path: '/v1/payments/instant-sepa-credit-transfers',
        httpStatus: 201,
        label: 'Initiate SCT Inst after VoP',
        detail: 'Only after the PSU accepted the VoP outcome. Log the VoP result with the payment for liability.',
        sampleId: 'bg-instant-payment-request',
      },
      {
        n: 5,
        from: 'aspsp',
        to: 'csm',
        layer: 'clearing',
        label: 'pacs.008 INST',
        detail: 'Same instant clearing path as the happy path.',
        messageShort: 'pacs.008',
        sampleId: 'pacs-008-sct-inst',
      },
      {
        n: 6,
        from: 'beneficiary',
        to: 'csm',
        layer: 'clearing',
        label: 'pacs.002 ACSC',
        detail: 'Instant settlement confirmation.',
        messageShort: 'pacs.002',
        sampleId: 'pacs-002-sct-inst',
        codes: ['ACSC'],
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────────────
  {
    id: 'sct-inst-recall',
    name: 'SCT Inst recall after settlement',
    standardId: 'sct-inst',
    category: 'exception',
    summary:
      'Instant payments can still be recalled (fraud, duplicate, technical). camt.056 → camt.029 → pacs.004, under tighter operational urgency than batch SCT.',
    useCase:
      'PSU reports fraud minutes after an instant credit. Funds may already be withdrawn — negative camt.029 is common.',
    actors: ['aspsp', 'csm', 'beneficiary'],
    tags: ['instant', 'sct-inst', 'recall', 'camt.056', 'fraud', 'exception'],
    steps: [
      {
        n: 1,
        from: 'aspsp',
        to: 'csm',
        layer: 'clearing',
        label: 'camt.056 recall (FRAD/DUPL)',
        detail: 'Raise quickly for fraud. Instant does not mean irreversible without a recall attempt.',
        messageShort: 'camt.056',
        sampleId: 'camt-056-recall',
        codes: ['FRAD', 'DUPL', 'TECH'],
      },
      {
        n: 2,
        from: 'csm',
        to: 'beneficiary',
        layer: 'clearing',
        label: 'Recall to creditor PSP',
        detail: 'Creditor checks whether funds remain and whether customer consent is needed.',
        messageShort: 'camt.056',
      },
      {
        n: 3,
        from: 'beneficiary',
        to: 'csm',
        layer: 'clearing',
        label: 'camt.029 resolution',
        detail: 'ACCR → return follows. NOAS / ARDT / LEGL → no money back; notify the originator PSP honestly.',
        messageShort: 'camt.029',
        sampleId: 'camt-029-resolution',
        codes: ['ACCR', 'NOAS', 'ARDT', 'LEGL'],
      },
      {
        n: 4,
        from: 'beneficiary',
        to: 'csm',
        layer: 'clearing',
        label: 'pacs.004 return (if accepted)',
        detail: 'Only after positive resolution. FOCR ties the return to the recall case.',
        messageShort: 'pacs.004',
        sampleId: 'pacs-004-return',
        codes: ['FOCR'],
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────────────
  {
    id: 'sepa-instant-timeout',
    name: 'SCT Inst timeout & investigation',
    standardId: 'sct-inst',
    category: 'exception',
    summary:
      'No pacs.002 inside the window. The debtor bank does not know whether the payment settled, and must resolve the ambiguity before telling anyone anything.',
    useCase:
      'The hardest state in instant payments. Guessing here creates either double payments or angry customers, so the investigation is mandatory.',
    actors: ['tpp', 'aspsp', 'csm', 'beneficiary'],
    tags: ['instant', 'sct-inst', 'timeout', 'pacs.028', 'tips', 'exception'],
    steps: [
      {
        n: 1,
        from: 'aspsp',
        to: 'csm',
        layer: 'clearing',
        label: 'pacs.008 submitted, timer starts',
        detail:
          'SCT Inst gives the whole chain about ten seconds from time of receipt. The debtor bank reserves the funds immediately.',
        messageShort: 'pacs.008',
        sampleId: 'pacs-008-sct-inst',
      },
      {
        n: 2,
        from: 'csm',
        to: 'aspsp',
        layer: 'clearing',
        label: 'Timeout — no answer',
        detail:
          'The payment is neither settled nor rejected. Hold the reservation; do not tell the PSU it failed; do not re-initiate.',
        codes: ['PDNG', 'TM01'],
      },
      {
        n: 3,
        from: 'aspsp',
        to: 'csm',
        layer: 'clearing',
        label: 'pacs.028 status request',
        detail: 'Formal enquiry quoting EndToEndId / TxId. Automated backoff, not a manual ticket.',
        messageShort: 'pacs.028',
        sampleId: 'pacs-028-sct-inst',
        codes: ['PDNG'],
      },
      {
        n: 4,
        from: 'beneficiary',
        to: 'aspsp',
        layer: 'clearing',
        label: 'Late pacs.002',
        detail:
          'Usually ACSC, occasionally RJCT. If still silent after the investigation window, treat as rejected per rulebook.',
        messageShort: 'pacs.002',
        sampleId: 'pacs-002-sct-inst',
        codes: ['ACSC', 'RJCT'],
      },
      {
        n: 5,
        from: 'aspsp',
        to: 'tpp',
        layer: 'api',
        method: 'GET',
        path: '/v1/payments/instant-sepa-credit-transfers/{paymentId}/status',
        httpStatus: 200,
        label: 'Status stays PDNG throughout',
        detail:
          'PISP clients must never treat a long PDNG as failure. Retrying the initiation here is how duplicate payments happen.',
        sampleId: 'bg-instant-payment-status-pdng',
        codes: ['PDNG'],
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────────────
  {
    id: 'vop-check',
    name: 'Verification of Payee',
    standardId: 'berlin-group',
    category: 'clearing',
    summary:
      'Name-and-IBAN matching before a transfer leaves. Mandatory across the euro area since the Instant Payments Regulation deadline in October 2025.',
    useCase:
      'A pre-flight check that changes PISP UX: the PSU may have to confirm a mismatch, which means your initiation screen needs a state it did not need before.',
    actors: ['tpp', 'aspsp', 'beneficiary'],
    tags: ['vop', 'confirmation of payee', 'acmt.023', 'fraud'],
    steps: [
      {
        n: 1,
        from: 'tpp',
        to: 'aspsp',
        layer: 'api',
        label: 'Payee name and IBAN captured',
        detail: 'The name you send for verification must be the one the PSU typed, not a normalised or cached version.',
      },
      {
        n: 2,
        from: 'aspsp',
        to: 'beneficiary',
        layer: 'clearing',
        label: 'acmt.023 verification request',
        detail: 'Routed via the VoP scheme directory. The response time budget is tight because it sits inside the payment journey.',
        messageShort: 'acmt.023',
        sampleId: 'acmt-023-vop',
      },
      {
        n: 3,
        from: 'beneficiary',
        to: 'aspsp',
        layer: 'clearing',
        label: 'acmt.024 report',
        detail:
          'Three outcomes: match, close match with the correct name returned, or no match. Close match is the interesting one — you must show the suggested name without leaking unrelated account data.',
        messageShort: 'acmt.024',
        sampleId: 'acmt-024-vop-report',
        codes: ['MTCH', 'CMTC', 'NMTC'],
      },
      {
        n: 4,
        from: 'aspsp',
        to: 'tpp',
        layer: 'api',
        label: 'Result surfaced to the PSU',
        detail:
          'On no-match the PSU has to explicitly accept the risk. If they proceed anyway, the liability shift means you want that consent logged.',
        codes: ['NMTC'],
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────────────
  {
    id: 'sic-chf-credit',
    name: 'SIC CHF customer credit (Regular Payment)',
    standardId: 'swiss-sps',
    category: 'clearing',
    summary:
      'Regular Swiss franc credit transfer settled via SIC RTGS in SNB central bank money during 24-hour continuous settlement cycles with daily cut-offs.',
    useCase:
      'Regular domestic CHF credit transfers under Swiss Payment Standards (SPS 2026). Requires 100% pre-funding; uncovered transfers enter the SIC wait file queue.',
    actors: ['psu', 'aspsp', 'rail', 'beneficiary'],
    tags: ['sic', 'chf', 'pain.001', 'pacs.008', 'swiss', 'regular'],
    steps: [
      {
        n: 1,
        from: 'psu',
        to: 'aspsp',
        layer: 'api',
        label: 'pain.001 CHF initiation',
        detail:
          'Customer-bank Swiss Payment Standards initiation. Supports Swiss QR-bill structured remittance data (QRR/SCOR); currency must be CHF.',
        messageShort: 'pain.001',
        sampleId: 'pain-001-sic-chf',
      },
      {
        n: 2,
        from: 'aspsp',
        to: 'psu',
        layer: 'api',
        label: 'pain.002 acceptance',
        detail: 'Bank accepts the instruction (ACTC) before submitting to the SIC rail. Rejection at this stage never enters SIC.',
        messageShort: 'pain.002',
        sampleId: 'pain-002-sic-chf',
        codes: ['ACTC', 'RJCT'],
      },
      {
        n: 3,
        from: 'aspsp',
        to: 'rail',
        layer: 'clearing',
        label: 'pacs.008 into SIC RTGS',
        detail:
          'Submitted to SIC with ClrSys=SIC. Verified for 100% liquidity coverage; queued in wait file if uncovered.',
        messageShort: 'pacs.008',
        sampleId: 'pacs-008-sic-chf',
      },
      {
        n: 4,
        from: 'rail',
        to: 'aspsp',
        layer: 'clearing',
        label: 'pacs.002 receipt',
        detail: 'SIC acknowledges receipt to the instructing participant before final settlement posting.',
        messageShort: 'pacs.002',
        sampleId: 'pacs-002-sic-chf',
        codes: ['RCVD', 'ACTC'],
      },
      {
        n: 5,
        from: 'rail',
        to: 'beneficiary',
        layer: 'clearing',
        label: 'Settled pacs.008 delivered',
        detail: 'After gross settlement in SNB sight deposit accounts, SIC delivers the credit transfer to the creditor bank participant.',
        messageShort: 'pacs.008',
      },
      {
        n: 6,
        from: 'beneficiary',
        to: 'rail',
        layer: 'clearing',
        label: 'Creditor bank pacs.002',
        detail: 'Receiving participant acknowledges. TxSts ACSC confirms final irrevocable central bank settlement.',
        messageShort: 'pacs.002',
        codes: ['ACSC'],
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────────────
  {
    id: 'sic-ip-instant',
    name: 'SIC Instant Payment (SIC IP)',
    standardId: 'swiss-sps',
    category: 'clearing',
    summary:
      'CHF instant payment on the SIX SIC5 platform — sub-10s (<10s) 24/7/365 settlement in SNB central bank money.',
    useCase:
      'Swiss instant A2A payments under SIX specifications. Mandated for major Swiss banks in Aug 2024 and all banks by end of 2026. 100% pre-funded without overdraft.',
    actors: ['aspsp', 'rail', 'beneficiary'],
    tags: ['sic', 'sic-ip', 'instant', 'chf', 'pacs.008', 'sic5'],
    steps: [
      {
        n: 1,
        from: 'aspsp',
        to: 'rail',
        layer: 'clearing',
        label: 'pacs.008 SIC IP submit',
        detail: 'Debtor bank reserves funds and submits pacs.008 with ClrSys=SIC and LclInstrm=INST/CHIP into the SIC5 IP service. Clock SLA <10s starts.',
        messageShort: 'pacs.008',
        sampleId: 'pacs-008-sic-ip',
      },
      {
        n: 2,
        from: 'rail',
        to: 'beneficiary',
        layer: 'clearing',
        label: 'Forward to creditor participant',
        detail: 'SIC IP routes to the receiving bank for immediate real-time credit decision.',
        messageShort: 'pacs.008',
      },
      {
        n: 3,
        from: 'beneficiary',
        to: 'rail',
        layer: 'clearing',
        label: 'pacs.002 ACSC',
        detail: 'Creditor bank accepts and credits account immediately. ACSC closes the instant happy path inside the <10s SLA.',
        messageShort: 'pacs.002',
        sampleId: 'pacs-002-sic-ip',
        codes: ['ACSC', 'RJCT'],
      },
      {
        n: 4,
        from: 'rail',
        to: 'aspsp',
        layer: 'clearing',
        label: 'Status back to debtor bank',
        detail: 'Debtor bank releases reservation as settled in SNB sight deposit accounts (or reverses on RJCT).',
        messageShort: 'pacs.002',
        codes: ['ACSC'],
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────────────
  {
    id: 'target2-regular-payment',
    name: 'TARGET2 / T2 regular credit transfer',
    standardId: 'sct-inst',
    category: 'clearing',
    summary:
      'Regular euro credit transfer settled in central bank money via TARGET2 (T2) RTGS during business operating hours, serving as the regular payment counterpart to TIPS instant payments.',
    useCase:
      'Regular / high-value euro credit transfers settled in Eurosystem central bank money when instant processing is not used or cut-off timing applies.',
    actors: ['aspsp', 'csm', 'beneficiary'],
    tags: ['target2', 't2', 'regular', 'clearing', 'pacs.008', 'central-bank'],
    steps: [
      {
        n: 1,
        from: 'aspsp',
        to: 'csm',
        layer: 'clearing',
        label: 'pacs.008 into T2 RTGS',
        detail: 'Debtor bank submits pacs.008 with ClrSys=TGT into TARGET2 (T2) RTGS for central bank money settlement during operating hours.',
        messageShort: 'pacs.008',
        sampleId: 'pacs-008-sct',
      },
      {
        n: 2,
        from: 'csm',
        to: 'aspsp',
        layer: 'clearing',
        label: 'pacs.002 settlement notification',
        detail:
          'After liquidity checks and gross settlement in Eurosystem central bank money, T2 notifies the instructing participant with pacs.002 (ACSC when subscribed, RJCT mandatory on rejection). Unlike STEP2 or TIPS, the creditor bank does not send a separate pacs.002 — T2 reports status directly to the debtor side.',
        messageShort: 'pacs.002',
        sampleId: 'pacs-002-accepted',
        codes: ['ACSC', 'RJCT'],
      },
      {
        n: 3,
        from: 'csm',
        to: 'beneficiary',
        layer: 'clearing',
        label: 'pacs.008 delivered to creditor PSP',
        detail: 'TARGET2 forwards the settled pacs.008 credit transfer to the receiving participant for account posting.',
        messageShort: 'pacs.008',
        sampleId: 'pacs-008-sct',
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────────────
  {
    id: 'sic-ip-timeout',
    name: 'SIC IP timeout and enquiry',
    standardId: 'swiss-sps',
    category: 'exception',
    summary:
      'No pacs.002 inside the SIC IP window. Hold the reservation and investigate with pacs.028 — never re-initiate blindly.',
    useCase: 'Same class of bug as SCT Inst timeouts, on the Swiss instant rail.',
    actors: ['aspsp', 'rail', 'beneficiary'],
    tags: ['sic-ip', 'timeout', 'pacs.028', 'exception', 'chf'],
    steps: [
      {
        n: 1,
        from: 'aspsp',
        to: 'rail',
        layer: 'clearing',
        label: 'pacs.008 submitted, timer starts',
        detail: 'Reservation placed. Clock is the SIC IP SLA, not next SIC batch.',
        messageShort: 'pacs.008',
        sampleId: 'pacs-008-sic-ip',
      },
      {
        n: 2,
        from: 'rail',
        to: 'aspsp',
        layer: 'clearing',
        label: 'Timeout — no answer',
        detail: 'Ambiguous state. Do not tell the customer it failed; do not submit a duplicate.',
        codes: ['PDNG'],
      },
      {
        n: 3,
        from: 'aspsp',
        to: 'rail',
        layer: 'clearing',
        label: 'pacs.028 status request',
        detail: 'Formal enquiry quoting EndToEndId / TxId from the original pacs.008.',
        messageShort: 'pacs.028',
        codes: ['PDNG'],
      },
      {
        n: 4,
        from: 'rail',
        to: 'aspsp',
        layer: 'clearing',
        label: 'Late pacs.002',
        detail: 'Usually ACSC or RJCT. If still silent after the investigation window, follow the SIC IP handbook rejection path.',
        messageShort: 'pacs.002',
        codes: ['ACSC', 'RJCT'],
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────────────
  {
    id: 'eurosic-eur-credit',
    name: 'euroSIC EUR clearing leg',
    standardId: 'swiss-sps',
    category: 'clearing',
    summary:
      'EUR credit transfer cleared through euroSIC for Swiss participants — ISO 20022 pacs on the euroSIC RTGS service.',
    useCase:
      'Swiss banks moving EUR via euroSIC rather than STEP2/TARGET. Same message types, euroSIC ClrSys and participant rules.',
    actors: ['aspsp', 'rail', 'beneficiary'],
    tags: ['eurosic', 'eur', 'pacs.008', 'swiss'],
    steps: [
      {
        n: 1,
        from: 'aspsp',
        to: 'rail',
        layer: 'clearing',
        label: 'pacs.008 into euroSIC',
        detail: 'Ccy=EUR and euroSIC clearing system code. Do not confuse with SIC CHF or SEPA CSM routing.',
        messageShort: 'pacs.008',
        sampleId: 'pacs-008-eurosic',
      },
      {
        n: 2,
        from: 'rail',
        to: 'aspsp',
        layer: 'clearing',
        label: 'pacs.002 receipt',
        detail: 'euroSIC acknowledges the instructing participant.',
        messageShort: 'pacs.002',
        codes: ['RCVD', 'ACTC'],
      },
      {
        n: 3,
        from: 'rail',
        to: 'beneficiary',
        layer: 'clearing',
        label: 'Settled delivery',
        detail: 'Creditor participant receives the settled pacs.008.',
        messageShort: 'pacs.008',
      },
      {
        n: 4,
        from: 'beneficiary',
        to: 'rail',
        layer: 'clearing',
        label: 'pacs.002 ACSC',
        detail: 'Settlement confirmed on the receiving side.',
        messageShort: 'pacs.002',
        sampleId: 'pacs-002-accepted',
        codes: ['ACSC'],
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────────────
  {
    id: 'wero-a2a-payment',
    name: 'A2A overlay payment (Wero sample)',
    standardId: 'wero',
    category: 'scheme',
    summary:
      'Retail A2A overlay: wallet intent, proxy resolve, ASPSP debit, SCT Inst settlement. Wero is the sample; Bizum, Payconiq, iDEAL, BLIK, Swish, Vipps MobilePay and TWINT follow the same split.',
    useCase:
      'Checkout or P2P where the UX is governed by EPI Company SE (Wero) but money moves on instant rails (SCT Inst). Option for direct or sub-participant bank routing.',
    actors: ['psu', 'scheme', 'aspsp', 'csm', 'beneficiary'],
    tags: ['wero', 'epi', 'instant', 'a2a', 'sct-inst'],
    steps: [
      {
        n: 1,
        from: 'psu',
        to: 'scheme',
        layer: 'api',
        label: 'Create Wero payment intent',
        detail: 'Amount, merchant/payee alias, and return URLs. Governed by EPI Company SE scheme rules.',
        sampleId: 'epi-company-wero-intent',
        codes: ['WERO_CREATED'],
      },
      {
        n: 2,
        from: 'scheme',
        to: 'scheme',
        layer: 'api',
        label: 'Resolve proxy / alias',
        detail: 'Phone or email → IBAN via the Wero directory. Still run VoP where regulation requires name check.',
        sampleId: 'wero-proxy-resolve',
        codes: ['WERO_PROXY_FOUND', 'WERO_PROXY_NOT_FOUND'],
      },
      {
        n: 3,
        from: 'psu',
        to: 'aspsp',
        layer: 'api',
        label: 'ASPSP authentication',
        detail: 'In-app confirm or bank SCA when the ASPSP requires it. Abandonment leaves the intent cancellable.',
        codes: ['WERO_AUTH_REQUIRED', 'WERO_AUTHORIZED'],
      },
      {
        n: 4,
        from: 'aspsp',
        to: 'csm',
        layer: 'clearing',
        label: 'SCT Inst pacs.008',
        detail: 'Underlying settlement leg is SCT Instant (or national instant), routed via direct participant or sub-participant BIC.',
        messageShort: 'pacs.008',
        sampleId: 'pacs-008-sct',
      },
      {
        n: 5,
        from: 'csm',
        to: 'aspsp',
        layer: 'clearing',
        label: 'pacs.002 ACSC',
        detail: 'Instant settlement confirmation. Only then should Wero show paid.',
        messageShort: 'pacs.002',
        sampleId: 'pacs-002-accepted',
        codes: ['ACSC', 'RJCT'],
      },
      {
        n: 6,
        from: 'scheme',
        to: 'psu',
        layer: 'api',
        label: 'Wero status COMPLETED',
        detail: 'Wallet UI updates from scheme status, which must lag the clearing confirmation — not lead it.',
        sampleId: 'wero-payment-completed',
        codes: ['WERO_COMPLETED'],
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────────────
  {
    id: 'hub-ip-transaction-flow',
    name: 'Instant Payment (IP) flow via Payment Hub & ILM',
    standardId: 'sct-inst',
    category: 'clearing',
    summary:
      'End-to-end instant payment flow through internal architecture: External Network > AGI (Access Gateway Interface) > Payment Hub (Finastra/Volante/FIS/Sopra/Maison) > ILM (Intraday Liquidity Management) > Core Banking / Settlement Engine.',
    useCase:
      'High-throughput instant payment processing detailing mandatory gateway/hub legs and optional ILM liquidity reservation vs direct Core Banking (T24) hold.',
    actors: ['external-network', 'agi', 'payment-hub', 'ilm', 'settlement'],
    tags: ['instant', 'ip', 'agi', 'payment hub', 'ilm', 'settlement', 'iso20022'],
    steps: [
      {
        n: 1,
        from: 'external-network',
        to: 'agi',
        layer: 'clearing',
        label: 'Inbound pacs.008 INST request',
        detail:
          'The external network (e.g. TIPS/RT1/SWIFT) delivers an inbound pacs.008 with LclInstrm=INST to the Access Gateway Interface (AGI).',
        messageShort: 'pacs.008',
        sampleId: 'pacs-008-sct-inst',
      },
      {
        n: 2,
        from: 'agi',
        to: 'payment-hub',
        layer: 'clearing',
        label: 'Validated pacs.008 payload relayed',
        detail:
          'AGI validates XML schema, security signatures, and mTLS headers, then routes the normalized pacs.008 message to the Payment Hub (Finastra/Volante/FIS/Sopra/Maison).',
        messageShort: 'pacs.008',
        sampleId: 'hub-internal-route',
      },
      {
        n: 3,
        from: 'payment-hub',
        to: 'ilm',
        layer: 'clearing',
        label: 'Real-time intraday liquidity check (Mandatory for direct participants; optional for pre-funded sub-participants)',
        detail:
          'The Payment Hub requests an instant intraday liquidity check and reservation from Intraday Liquidity Management (ILM) to ensure adequate central bank / participant coverage.',
        messageShort: 'pacs.008',
        sampleId: 'ilm-reservation-check',
        codes: ['ACSP', 'AM04'],
        participation: 'direct',
      },
      {
        n: 4,
        from: 'ilm',
        to: 'payment-hub',
        layer: 'clearing',
        label: 'Liquidity reserved & approved',
        detail:
          'ILM verifies current intraday positions, reserves liquidity for the instant transaction, and returns confirmation to the Payment Hub.',
        codes: ['ACSP'],
        participation: 'direct',
      },
      {
        n: 5,
        from: 'payment-hub',
        to: 'settlement',
        layer: 'clearing',
        label: 'Core Banking (T24) hold & settlement execution',
        detail:
          'Payment Hub dispatches the pacs.008 instruction to the Core Banking System (T24) / Settlement Engine to place fund reservation hold and post ledger entries.',
        messageShort: 'pacs.008',
        sampleId: 'cbs-fund-hold',
      },
      {
        n: 6,
        from: 'settlement',
        to: 'payment-hub',
        layer: 'clearing',
        label: 'pacs.002 ACSC final status',
        detail:
          'Settlement Engine / Core Banking executes immediate settlement and returns a pacs.002 status report with TxSts=ACSC (Accepted Settlement Completed).',
        messageShort: 'pacs.002',
        sampleId: 'pacs-002-sct-inst',
        codes: ['ACSC'],
      },
      {
        n: 7,
        from: 'payment-hub',
        to: 'ilm',
        layer: 'clearing',
        label: 'Commit liquidity reservation',
        detail:
          'Payment Hub notifies ILM that settlement is complete, converting the temporary reservation into a final settled intraday ledger position.',
        codes: ['ACSC'],
        participation: 'direct',
      },
      {
        n: 8,
        from: 'payment-hub',
        to: 'agi',
        layer: 'clearing',
        label: 'pacs.002 ACSC response forwarded',
        detail:
          'Payment Hub forwards the positive pacs.002 confirmation back to AGI for outbound transmission.',
        messageShort: 'pacs.002',
        sampleId: 'pacs-002-sct-inst',
        codes: ['ACSC'],
      },
      {
        n: 9,
        from: 'agi',
        to: 'external-network',
        layer: 'clearing',
        label: 'Outbound pacs.002 ACSC acknowledgement',
        detail:
          'AGI delivers the pacs.002 ACSC confirmation across the external network back to the instructing party within the instant SLA.',
        messageShort: 'pacs.002',
        sampleId: 'pacs-002-sct-inst',
        codes: ['ACSC'],
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────────────
  {
    id: 'hub-non-ip-transaction-flow',
    name: 'Non-Instant Payment (Standard Batch) flow via Payment Hub & ILM',
    standardId: 'berlin-group',
    category: 'clearing',
    summary:
      'End-to-end non-instant (standard batch / credit transfer) payment flow through internal architecture: External Network > AGI > Payment Hub > ILM > Core Banking (T24) / Settlement Engine.',
    useCase:
      'Standard payment processing detailing mandatory routing legs and optional Sub Participant clearing vs direct clearing.',
    actors: ['external-network', 'agi', 'payment-hub', 'ilm', 'settlement'],
    tags: ['non-ip', 'batch', 'agi', 'payment hub', 'ilm', 'settlement', 'iso20022'],
    steps: [
      {
        n: 1,
        from: 'external-network',
        to: 'agi',
        layer: 'clearing',
        label: 'Inbound pacs.008 standard credit transfer',
        detail:
          'The external network (STEP2 / SWIFT / CSM) delivers a batch or standard credit transfer pacs.008 to the Access Gateway Interface (AGI).',
        messageShort: 'pacs.008',
        sampleId: 'pacs-008-sct',
      },
      {
        n: 2,
        from: 'agi',
        to: 'payment-hub',
        layer: 'clearing',
        label: 'Validated pacs.008 payload relayed',
        detail:
          'AGI validates XML well-formedness and schema rules, then hands off the instruction to the Payment Hub (Finastra/Volante/FIS/Sopra/Maison).',
        messageShort: 'pacs.008',
        sampleId: 'hub-internal-route',
      },
      {
        n: 3,
        from: 'payment-hub',
        to: 'ilm',
        layer: 'clearing',
        label: 'Limit check & batch liquidity queuing (Optional for pre-funded sub-participants; mandatory for direct clearing members)',
        detail:
          'Payment Hub checks intraday liquidity limits with ILM. Since it is non-instant, if liquidity is tight, the transaction is queued for the next cutoff window rather than rejected immediately.',
        messageShort: 'pacs.008',
        sampleId: 'ilm-reservation-check',
        codes: ['ACTC', 'PDNG'],
        participation: 'direct',
      },
      {
        n: 4,
        from: 'ilm',
        to: 'payment-hub',
        layer: 'clearing',
        label: 'Liquidity allocated at batch cutoff',
        detail:
          'During the scheduled intraday settlement window, ILM allocates required intraday liquidity for the batch and notifies the Payment Hub.',
        codes: ['ACSP'],
        participation: 'direct',
      },
      {
        n: 5,
        from: 'payment-hub',
        to: 'settlement',
        layer: 'clearing',
        label: 'Batch settlement submission & Core Banking posting',
        detail:
          'Payment Hub submits the accumulated pacs.008 instructions to the Settlement Engine / Core Banking System (T24) for ledger posting.',
        messageShort: 'pacs.008',
        sampleId: 'cbs-fund-hold',
      },
      {
        n: 6,
        from: 'settlement',
        to: 'payment-hub',
        layer: 'clearing',
        label: 'pacs.002 ACSC batch confirmation',
        detail:
          'Settlement Engine executes batch posting and returns a pacs.002 report indicating ACSC (Accepted Settlement Completed).',
        messageShort: 'pacs.002',
        sampleId: 'pacs-002-accepted',
        codes: ['ACSC'],
      },
      {
        n: 7,
        from: 'payment-hub',
        to: 'ilm',
        layer: 'clearing',
        label: 'Update intraday liquidity position',
        detail:
          'Payment Hub sends final settlement details to ILM to update the institution intraday liquidity ledger and release remaining holds.',
        codes: ['ACSC'],
        participation: 'direct',
      },
      {
        n: 8,
        from: 'payment-hub',
        to: 'agi',
        layer: 'clearing',
        label: 'pacs.002 ACSC response forwarded',
        detail:
          'Payment Hub passes the pacs.002 confirmation report back to AGI.',
        messageShort: 'pacs.002',
        sampleId: 'pacs-002-accepted',
        codes: ['ACSC'],
      },
      {
        n: 9,
        from: 'agi',
        to: 'external-network',
        layer: 'clearing',
        label: 'Outbound pacs.002 ACSC delivered',
        detail:
          'AGI transmits the final pacs.002 ACSC report across the external network to complete the non-instant payment lifecycle.',
        messageShort: 'pacs.002',
        sampleId: 'pacs-002-accepted',
        codes: ['ACSC'],
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────────────
  {
    id: 'bg-payment-cancellation',
    name: 'Payment cancellation before settlement',
    standardId: 'berlin-group',
    category: 'exception',
    summary:
      'TPP cancels a payment that is still cancellable; ASPSP maps DELETE to camt.055 and returns a terminal status.',
    useCase:
      'User abandons checkout after initiation. After settlement you need a recall (camt.056), not DELETE.',
    actors: ['psu', 'tpp', 'aspsp', 'csm'],
    tags: ['cancellation', 'delete', 'camt.055', 'berlin group', 'exception'],
    steps: [
      {
        n: 1,
        from: 'psu',
        to: 'tpp',
        layer: 'api',
        label: 'User cancels checkout',
        detail: 'Only safe while status is RCVD / PDNG / ACTC. ACSP/ACSC means the money is already moving or gone.',
        codes: ['RCVD', 'PDNG', 'ACTC'],
      },
      {
        n: 2,
        from: 'tpp',
        to: 'aspsp',
        layer: 'api',
        method: 'DELETE',
        path: '/v1/payments/sepa-credit-transfers/{paymentId}',
        httpStatus: 202,
        label: 'DELETE payment resource',
        detail:
          'Berlin Group cancellation. Some banks return 204, others 202 with a cancellation resource. CANCULATION_INVALID if already executed.',
        sampleId: 'bg-payment-cancel-request',
        codes: ['CANC', 'CANCELLATION_INVALID', 'RESOURCE_UNKNOWN'],
      },
      {
        n: 3,
        from: 'aspsp',
        to: 'csm',
        layer: 'clearing',
        label: 'camt.055 if still pre-clearing',
        detail:
          'Customer cancellation request toward the bank’s payment engine. If already submitted to the CSM, this becomes a recall path instead.',
        messageShort: 'camt.055',
        sampleId: 'camt-055-cancel',
      },
      {
        n: 4,
        from: 'aspsp',
        to: 'tpp',
        layer: 'api',
        method: 'GET',
        path: '/v1/payments/sepa-credit-transfers/{paymentId}/status',
        httpStatus: 200,
        label: 'Status CANC',
        detail: 'Terminal cancelled. Do not retry the same EndToEndId as a new initiation without a new id.',
        sampleId: 'bg-payment-cancel-status',
        codes: ['CANC'],
      },
    ],
  },
];

export const flowById = (id: string) => FLOWS.find((f) => f.id === id);

/** URL / UI value for clearing membership view. Default is direct participant. */
export function parseFlowParticipation(raw: string | null): FlowParticipation {
  return raw === 'sub' ? 'sub' : 'direct';
}

/** True when the flow has at least one step that differs by participation. */
export function flowHasParticipationModes(flow: Flow): boolean {
  return flow.steps.some((s) => s.participation != null);
}

/**
 * Project a flow for a participation view: drop role-specific steps and
 * actors that no longer appear on any remaining hop.
 */
export function withFlowParticipation(flow: Flow, mode: FlowParticipation): Flow {
  if (!flowHasParticipationModes(flow)) return flow;
  const steps = flow.steps.filter((s) => s.participation == null || s.participation === mode);
  const used = new Set<ActorId>();
  for (const step of steps) {
    used.add(step.from);
    used.add(step.to);
  }
  const actors = flow.actors.filter((id) => used.has(id));
  return { ...flow, actors, steps };
}

/** Flows and steps that actually put this ISO message on the wire. */
export function usagesOfMessage(short: string): { flow: Flow; steps: FlowStep[] }[] {
  const out: { flow: Flow; steps: FlowStep[] }[] = [];
  for (const flow of FLOWS) {
    const steps = flow.steps.filter((s) => s.messageShort === short);
    if (steps.length) out.push({ flow, steps });
  }
  return out;
}

/** Flows and steps that reference this sample (XML or its JSON companion). */
export function usagesOfSample(sampleId: string): { flow: Flow; steps: FlowStep[] }[] {
  const ids = sampleIdAliases(sampleId);
  const out: { flow: Flow; steps: FlowStep[] }[] = [];
  for (const flow of FLOWS) {
    const steps = flow.steps.filter((s) => s.sampleId != null && ids.has(s.sampleId));
    if (steps.length) out.push({ flow, steps });
  }
  return out;
}

function sampleIdAliases(sampleId: string): Set<string> {
  const ids = new Set([sampleId]);
  if (sampleId.endsWith('-json')) ids.add(sampleId.slice(0, -5));
  else ids.add(`${sampleId}-json`);
  return ids;
}

/** Distinct ISO message shorts in diagram order. */
export function isoMessagesInFlow(flow: Flow): string[] {
  const ordered: string[] = [];
  const seen = new Set<string>();
  for (const step of flow.steps) {
    if (!step.messageShort || seen.has(step.messageShort)) continue;
    seen.add(step.messageShort);
    ordered.push(step.messageShort);
  }
  return ordered;
}

/** Distinct sample ids referenced by steps, in diagram order. */
export function sampleIdsInFlow(flow: Flow): string[] {
  const ordered: string[] = [];
  const seen = new Set<string>();
  for (const step of flow.steps) {
    if (!step.sampleId || seen.has(step.sampleId)) continue;
    seen.add(step.sampleId);
    ordered.push(step.sampleId);
  }
  return ordered;
}

/** True when the Try editor has samples for messages in this flow. */
export function flowSupportsTryEditor(flow: Flow): boolean {
  return flow.steps.some((s) => s.messageShort !== undefined);
}

export const CATEGORY_LABELS: Record<Flow['category'], string> = {
  'account-information': 'Account information',
  'payment-initiation': 'Payment initiation',
  'funds-confirmation': 'Funds confirmation',
  clearing: 'Clearing & settlement',
  exception: 'Exception handling',
  scheme: 'Payment schemes',
};
